import React from "react";
import { MapPin, Clock, Phone, Mail, ArrowRight, Instagram, Facebook } from "lucide-react";
import { useContent } from "../contexts/ContentContext";
import { useReservation } from "./ReservationDialog";

const DAY_LABELS = {
    ca: { mon: "Dilluns", tue: "Dimarts", wed: "Dimecres", thu: "Dijous", fri: "Divendres", sat: "Dissabte", sun: "Diumenge" },
    es: { mon: "Lunes", tue: "Martes", wed: "Miércoles", thu: "Jueves", fri: "Viernes", sat: "Sábado", sun: "Domingo" },
    en: { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" },
};

export default function Contact() {
    const { t, content, lang } = useContent();
    const { setOpen } = useReservation();
    const info = content?.contact_info || {};
    const hours = content?.hours || {};
    const days = DAY_LABELS[lang] || DAY_LABELS.ca;

    return (
        <section id="contact" className="relative py-24 md:py-40 px-4 sm:px-6 lg:px-8 bg-brand-cream" data-testid="contact-section">
            <div className="max-w-7xl mx-auto">
                <div className="max-w-3xl mb-16">
                    <span className="kicker">{t?.contact?.kicker}</span>
                    <h2 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl text-brand-navy leading-[1.05]" data-testid="contact-title">
                        {t?.contact?.title}
                    </h2>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                    <div className="lg:col-span-5 space-y-8">
                        <div className="flex gap-5" data-testid="contact-address">
                            <div className="w-11 h-11 rounded-full bg-white border border-brand-navy/10 flex items-center justify-center flex-shrink-0">
                                <MapPin size={18} className="text-brand-terracotta" />
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-[0.2em] text-brand-blue mb-1">{t?.contact?.address_label}</div>
                                <div className="font-display text-xl text-brand-navy">{info.address}</div>
                            </div>
                        </div>

                        <div className="flex gap-5" data-testid="contact-phone">
                            <div className="w-11 h-11 rounded-full bg-white border border-brand-navy/10 flex items-center justify-center flex-shrink-0">
                                <Phone size={18} className="text-brand-terracotta" />
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-[0.2em] text-brand-blue mb-1">{t?.contact?.phone_label}</div>
                                <a href={`tel:${info.phone}`} className="font-display text-xl text-brand-navy hover:text-brand-terracotta">
                                    {info.phone}
                                </a>
                            </div>
                        </div>

                        <div className="flex gap-5" data-testid="contact-email">
                            <div className="w-11 h-11 rounded-full bg-white border border-brand-navy/10 flex items-center justify-center flex-shrink-0">
                                <Mail size={18} className="text-brand-terracotta" />
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-[0.2em] text-brand-blue mb-1">{t?.contact?.email_label}</div>
                                <a href={`mailto:${info.email}`} className="font-display text-xl text-brand-navy hover:text-brand-terracotta break-all">
                                    {info.email}
                                </a>
                            </div>
                        </div>

                        <div className="flex gap-5" data-testid="contact-hours">
                            <div className="w-11 h-11 rounded-full bg-white border border-brand-navy/10 flex items-center justify-center flex-shrink-0">
                                <Clock size={18} className="text-brand-terracotta" />
                            </div>
                            <div className="flex-1">
                                <div className="text-xs uppercase tracking-[0.2em] text-brand-blue mb-3">{t?.contact?.hours_label}</div>
                                <dl className="space-y-1.5 text-sm">
                                    {["mon", "tue", "wed", "thu", "fri", "sat", "sun"].map((d) => (
                                        <div key={d} className="flex justify-between gap-4 border-b border-brand-navy/5 pb-1.5">
                                            <dt className="text-brand-navy font-medium">{days[d]}</dt>
                                            <dd className="text-slate-600">{hours[d] || "—"}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        </div>

                        <div className="flex gap-3 pt-4">
                            {info.instagram && (
                                <a href={info.instagram} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full bg-white border border-brand-navy/10 hover:bg-brand-navy hover:text-white flex items-center justify-center text-brand-navy transition-colors" aria-label="Instagram" data-testid="social-instagram">
                                    <Instagram size={18} />
                                </a>
                            )}
                            {info.facebook && (
                                <a href={info.facebook} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full bg-white border border-brand-navy/10 hover:bg-brand-navy hover:text-white flex items-center justify-center text-brand-navy transition-colors" aria-label="Facebook" data-testid="social-facebook">
                                    <Facebook size={18} />
                                </a>
                            )}
                        </div>

                        <button
                            type="button"
                            onClick={() => setOpen(true)}
                            className="btn-primary mt-6"
                            data-testid="contact-btn-reserve"
                        >
                            {t?.contact?.reserve_cta}
                            <ArrowRight size={16} />
                        </button>
                    </div>

                    <div className="lg:col-span-7">
                        <div className="relative w-full aspect-[4/3] lg:aspect-auto lg:h-full min-h-[420px] rounded-2xl overflow-hidden border border-brand-navy/10 shadow-xl">
                            <iframe
                                title="Zarautz BCN location"
                                src={info.map_embed}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                className="absolute inset-0 w-full h-full"
                                data-testid="contact-map"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
